import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Shift } from '@/openapi';
import { ScheduleConfigService } from '@/openapi';
import { queryKeys } from '@/util/queryKeys';

interface UpdateStandardShiftMutationParams {
  shift: Shift;
  standard: boolean;
}

export const useUpdateStandardShiftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mutationVars: UpdateStandardShiftMutationParams) => {
      const { shift, standard } = mutationVars;
      return ScheduleConfigService.scheduleConfigServiceUpdateShift({
        path: { shiftId: shift.id },
        body: {
          ...shift,
          standard,
        },
      });
    },
    onMutate: async (mutationVars: UpdateStandardShiftMutationParams) => {
      // Cancel any outgoing refetches, snapshot current values
      await queryClient.cancelQueries({ queryKey: queryKeys.shifts.list() });
      const previousShifts = queryClient.getQueryData<Shift[]>(
        queryKeys.shifts.list()
      );

      if (previousShifts) {
        const newShifts = previousShifts.map((shift) =>
          shift.id === mutationVars.shift.id
            ? { ...shift, standard: mutationVars.standard }
            : shift
        );
        queryClient.setQueryData(queryKeys.shifts.list(), newShifts);
      }

      return { previousShifts };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousShifts) {
        queryClient.setQueryData(
          queryKeys.shifts.list(),
          context.previousShifts
        );
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.shifts.list() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.holidays.withShifts(),
        }),
      ]);
    },
  });
};
